import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { X } from 'lucide-react';

export const PainPoints3D = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const painPoints = [
    'Training hard for months with nothing measurable to show for it',
    'Nutrition that works until Wednesday — then falls apart with one busy day',
    'No clear path forward — just effort, frustration, and starting over'
  ];

  return (
    <section ref={ref} className="bg-panel py-24 md:py-32 px-6 overflow-hidden">
      <div className="max-w-5xl mx-auto">
        <motion.div 
          className="text-center mb-16 space-y-6"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.p 
            className="font-accent text-xl md:text-2xl italic text-gold"
            initial={{ opacity: 0, y: -50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Sound Familiar?
          </motion.p>
          <motion.h2 
            className="font-hero text-3xl md:text-5xl lg:text-6xl uppercase tracking-wide text-cream leading-tight"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            style={{ fontWeight: 700 }}
          >
            You're Not
            <br />
            <span className="text-gold italic font-accent">Inconsistent.</span>
            <br />
            You're Missing a System.
          </motion.h2>
          <motion.p
            className="font-body text-base md:text-lg text-[#e8e6df]/80 max-w-3xl mx-auto leading-relaxed font-light"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            You show up. You put in the effort. But without a structure that connects your training, nutrition, and habits — nothing compounds. You keep restarting while life keeps getting in the way.
          </motion.p>
        </motion.div>

        <div className="space-y-6">
          {painPoints.map((point, index) => (
            <PainPointCard key={index} point={point} index={index} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
};

const PainPointCard = ({ point, index, inView }) => {
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
  const cardRef = React.useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 15;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -15;
    setMousePosition({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -100, rotateY: -30 }}
      animate={inView ? { opacity: 1, x: 0, rotateY: 0 } : {}}
      transition={{ 
        delay: 0.6 + index * 0.15,
        duration: 0.8,
        type: "spring",
        stiffness: 80
      }}
      style={{ perspective: '1500px' }}
    >
      <motion.div
        ref={cardRef}
        className="bg-dark p-6 md:p-8 border border-red-900/30 hover:border-red-900/60 flex items-start gap-6 cursor-pointer group"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        animate={{
          rotateX: mousePosition.y * 0.3,
          rotateY: mousePosition.x * 0.3,
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        <motion.div 
          className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-red-900/20 border border-red-900/50 flex items-center justify-center group-hover:bg-red-900/40 transition-colors duration-300"
          style={{ transform: 'translateZ(40px)' }}
          whileHover={{ rotate: 90, scale: 1.1 }}
        >
          <X className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
        </motion.div>
        <p 
          className="font-body text-base md:text-lg text-[#e8e6df] leading-relaxed font-light pt-2"
          style={{ transform: 'translateZ(20px)' }}
        >
          {point}
        </p>
      </motion.div>
    </motion.div>
  );
};
